import React, { useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { axiosMaster, axiosSalary } from "../api/axiosInstance";
import AddEditAttendancePolicy from "./AddEditAttendancePolicy";

const AttendancePolicy = () => {
  /* ================= USER ================= */
  let storedUser = {};

  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const groupId = storedUser?.groupId;

  /* ================= STATE ================= */
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestId, setSelectedRestId] = useState("");
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);

  /* ================= PUNCH HANDLING MODE LABEL MAP ================= */
  const punchModeLabels = {
    AUTO_PUNCH_OUT: "Auto Punch Out",
    SUSPENSE: "Suspense",
  };

  const formatPunchMode = (mode) => punchModeLabels[mode] || mode || "-";

  /* ================= FETCH RESTAURANTS ================= */
  const fetchRestaurants = async () => {
    try {
      const res = await axiosMaster.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );

      const rows = res.data || [];

      setRestaurants(rows);

      if (rows.length > 0) {
        setSelectedRestId(rows[0].restId);
      }
    } catch (err) {
      console.error("Restaurant fetch error:", err);
    }
  };

  /* ================= FETCH POLICY ================= */
  const fetchPolicy = async (restId) => {
    if (!restId) return;

    try {
      setLoading(true);

      const res = await axiosSalary.get(
        `/AttendancePolicyMaster/GetByGroupRestId?groupId=${groupId}&restId=${restId}`
      );

      setPolicy(res.data || null);
    } catch (err) {
      console.error("Policy fetch error:", err);
      setPolicy(null);
    } finally {
      setLoading(false);
    }
  };

  /* ================= LOAD ================= */
  useEffect(() => {
    if (groupId) {
      fetchRestaurants();
    }
  }, [groupId]);

  useEffect(() => {
    if (selectedRestId) {
      fetchPolicy(selectedRestId);
    }
  }, [selectedRestId]);

  const handleSavePolicy = async (payload) => {
    try {
      await axiosSalary.post(
        "/AttendancePolicyMaster/UpsertAttendancePolicy",
        payload
      );

      setFormOpen(false);

      if (selectedRestId) {
        fetchPolicy(selectedRestId);
      }
    } catch (err) {
      console.error("Save policy error:", err);

      alert(
        err?.response?.data?.message || "Error saving attendance policy"
      );
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-5 border border-slate-200 min-h-[calc(100vh-120px)]">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h2 className="text-lg md:text-xl font-bold text-slate-800">
          Attendance Policy
        </h2>

        <div className="flex flex-wrap gap-2">
          <select
            value={selectedRestId}
            onChange={(e) => setSelectedRestId(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm min-w-[250px]"
          >
            {restaurants.map((r) => (
              <option key={r.restId} value={r.restId}>
                {r.restaurantName}
              </option>
            ))}
          </select>

          <button
            onClick={() => setFormOpen(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
          >
            <FaEdit />
            Edit Policy
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="border border-slate-300 rounded-lg overflow-hidden">
        <table className="w-full text-sm border-collapse">
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="2" className="text-center p-6">
                  Loading...
                </td>
              </tr>
            ) : !policy ? (
              <tr>
                <td colSpan="2" className="text-center p-6">
                  No Policy Found
                </td>
              </tr>
            ) : (
              <>
                <tr>
                  <td className="border p-3 font-medium bg-slate-50 w-[40%]">
                    Full Day Minimum %
                  </td>
                  <td className="border p-3">{policy.fullDayMinPercent}%</td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Half Day Minimum %
                  </td>
                  <td className="border p-3">{policy.halfDayMinPercent}%</td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Quarter Day Minimum %
                  </td>
                  <td className="border p-3">
                    {policy.quarterDayMinPercent}%
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Quarter Day Enabled
                  </td>
                  <td className="border p-3">
                    {policy.isQuarterDayEnabled ? "Yes" : "No"}
                  </td>
                </tr>

                {/* ===== PUNCH HANDLING MODES ===== */}
                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Open Shift Punch Handling Mode
                  </td>
                  <td className="border p-3">
                    {formatPunchMode(policy.openShiftPunchHandlingMode)}
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Reporting Shift Punch Handling Mode
                  </td>
                  <td className="border p-3">
                    {formatPunchMode(policy.reportingShiftPunchHandlingMode)}
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Closed Shift Punch Handling Mode
                  </td>
                  <td className="border p-3">
                    {formatPunchMode(policy.closedShiftPunchHandlingMode)}
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Break Shift Punch Handling Mode
                  </td>
                  <td className="border p-3">
                    {formatPunchMode(policy.breakShiftPunchHandlingMode)}
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Monthly Leave Credit
                  </td>
                  <td className="border p-3">{policy.monthlyLeaveCredit}</td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Weekly Off Paid
                  </td>
                  <td className="border p-3">
                    {policy.weeklyOffPaid ? "Yes" : "No"}
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Salary Calculation Mode
                  </td>
                  <td className="border p-3">
                    {policy.salaryCalculationMode}
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Leave Settlement Mode
                  </td>
                  <td className="border p-3">
                    {policy.unusedLeaveSettlementMode}
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Leave Encash Rate %
                  </td>
                  <td className="border p-3">
                    {policy.leaveEncashRatePercent}%
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Remarks
                  </td>
                  <td className="border p-3">{policy.remarks}</td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Status
                  </td>
                  <td className="border p-3">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        policy.status
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {policy.status ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>

                <tr>
                  <td className="border p-3 font-medium bg-slate-50">
                    Entry Date
                  </td>
                  <td className="border p-3">{policy.entryDate}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <AddEditAttendancePolicy
          editData={policy}
          selectedRestId={selectedRestId}
          groupId={groupId}
          onClose={() => setFormOpen(false)}
          onSave={handleSavePolicy}
        />
      )}
    </div>
  );
};

export default AttendancePolicy;